/**
 * Design tokens para glassmorphism, gradientes, e animações
 * Baseado no redesign moderno com shadcn/ui
 */

// Status Colors - using CSS variables from theme system
export const statusColors = {
  primary: 'hsl(var(--primary))',
  success: 'hsl(var(--success))',
  warning: 'hsl(var(--warning))',
  info: 'hsl(var(--info))',
  destructive: 'hsl(var(--destructive))',
  muted: 'hsl(var(--muted))',
} as const;

// Icon Background Colors
export const iconBackgrounds = {
  primary: 'bg-primary/10',
  success: 'bg-success/10',
  warning: 'bg-warning/10',
  info: 'bg-info/10',
  destructive: 'bg-destructive/10',
  muted: 'bg-muted/10',
} as const;

// Icon Text Colors
export const iconTextColors = {
  primary: 'text-primary',
  success: 'text-success',
  warning: 'text-warning',
  info: 'text-info',
  destructive: 'text-destructive',
  muted: 'text-muted-foreground',
} as const;

/**
 * Glassmorphism effect classes
 * Consolidated single source of truth for all glass effects across the app
 * Merged from legacy glassmorphismClasses and modern glass patterns
 */
export const glass = {
  // === Base Glass Variants (from legacy glassmorphismClasses) ===
  /** Standard card glass effect - most commonly used */
  card: 'backdrop-blur-md bg-card/95 border border-primary/20',
  /** Card hover state with border glow */
  cardHover: 'hover:border-primary/40 hover:shadow-xl transition-all duration-300',
  /** Overlay glass for modals/dialogs */
  overlay: 'backdrop-blur-sm bg-background/80',
  /** Intense glass effect for prominent elements */
  intense: 'backdrop-blur-lg bg-card/90 border border-primary/30',
  /** Subtle glass effect for secondary elements */
  subtle: 'backdrop-blur-sm bg-card/30 border border-primary/10',
  /** Input field glass effect */
  input: 'backdrop-blur-sm bg-background/50 border border-primary/20',

  // === Modern Card Variants (BusinessBio patterns) ===
  /** Modern card with rounded corners and shadow */
  cardModern: 'border-border bg-card/50 backdrop-blur-xl shadow-xl rounded-xl overflow-hidden',
  /** Modern hover state - color transition */
  cardModernHover: 'hover:bg-card/60 transition-colors duration-200',

  // === Subtle Cards - Internal cards with gradient ===
  /** Subtle card with primary gradient */
  cardSubtle:
    'border-border bg-gradient-to-br from-primary/5 to-transparent backdrop-blur-sm h-full',
  /** Subtle card with secondary gradient */
  cardSubtleAlt:
    'border-border bg-gradient-to-br from-secondary/5 to-transparent backdrop-blur-sm h-full',

  // === Inner Cards - Nested content ===
  /** Inner card for nested sections */
  cardInner:
    'border-border bg-gradient-to-br from-card/60 to-card/30 backdrop-blur-sm rounded-lg',
  /** Inner card with primary highlight */
  cardInnerHighlight:
    'border-border bg-gradient-to-br from-primary/20 to-primary/5 backdrop-blur-sm rounded-lg',

  // === Special Cards ===
  /** Hero/profile card with gradient */
  cardHero:
    'border-border bg-gradient-to-br from-primary/10 via-card/80 to-card/60 backdrop-blur-xl shadow-2xl overflow-hidden relative',
  /** Search card with elevated shadow */
  cardSearch: 'border-border bg-card/50 backdrop-blur-xl shadow-2xl',

  // === Accordion ===
  /** Accordion trigger styling */
  trigger: 'px-6 hover:no-underline hover:bg-accent/50 transition-colors',
  /** Accordion content padding */
  content: 'px-6 pb-6',

  // === Separators ===
  /** Glass separator line */
  separator: 'bg-border',

  // === Avatar Effects ===
  /** Avatar glow effect with hover */
  avatarGlow:
    'absolute inset-0 bg-gradient-to-br from-primary/30 to-secondary/30 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300',
  /** Avatar border styling */
  avatarBorder: 'border-4 border-border shadow-2xl',

  // === Icon Containers ===
  /** Standard icon container */
  iconContainer: 'p-2 rounded-lg bg-primary/20',
  /** Small icon container */
  iconContainerSm: 'p-1.5 rounded-md bg-primary/20',

  // === Overlays ===
  /** Gradient overlay for decorative effects */
  gradientOverlay:
    'absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 pointer-events-none',

  // === States ===
  /** Empty state container */
  emptyState: 'p-4 rounded-full bg-primary/10',
  /** Skeleton loading state */
  skeleton: 'bg-border',

  // === Interactive States ===
  /** Hover state for glass elements */
  hover: 'hover:bg-card/95 hover:border-primary/40',
  /** Active/pressed state */
  active: 'active:bg-card/98 active:scale-[0.99]',
  /** Focus visible state */
  focus: 'focus-visible:ring-2 focus-visible:ring-primary/50',

  // === Inner Highlights ===
  /** White inner highlight gradient */
  innerHighlight: 'bg-gradient-to-br from-white/5 to-transparent',
  /** Primary inner highlight gradient */
  primaryHighlight: 'bg-gradient-to-br from-primary/20 to-primary/5',

  // === Lobby Screen Variants ===
  /** Lobby card with strong blur for readability over background */
  cardLobby: 'backdrop-blur-xl bg-card/95 border border-border/30 shadow-2xl rounded-2xl',
  /** Lobby status bar */
  statusBar: 'backdrop-blur-lg bg-background/90 border-b border-border/40',
  /** Lobby overlay gradient */
  lobbyOverlay: 'bg-gradient-to-b from-background/60 via-background/70 to-background/80',
  /** CTA hero button gradient */
  ctaHero: 'bg-gradient-to-r from-primary to-primary/80 shadow-2xl shadow-primary/50',
} as const;

/**
 * Event status colors for badges and indicators
 */
export const eventStatus = {
  live: {
    badge: 'bg-destructive text-destructive-foreground animate-pulse',
    dot: 'bg-destructive',
    border: 'border-destructive/50',
    text: 'text-destructive',
  },
  upcoming: {
    badge: 'bg-warning text-foreground',
    dot: 'bg-warning',
    border: 'border-warning/50',
    text: 'text-warning',
  },
  finished: {
    badge: 'bg-muted-foreground text-background',
    dot: 'bg-muted-foreground',
    border: 'border-border',
    text: 'text-muted-foreground',
  },
} as const;

/**
 * @deprecated Use `glass` object instead. This alias is kept for backward compatibility.
 * Migration: Replace `glassmorphismClasses.X` with `glass.X`
 */
export const glassmorphismClasses = glass;

/**
 * Animation delays for staggered animations
 */
export const staggerDelays = {
  first: { delay: 0.1 },
  second: { delay: 0.2 },
  third: { delay: 0.3 },
  fourth: { delay: 0.4 },
  fifth: { delay: 0.5 },
} as const;

/**
 * Motion animation presets for cards
 */
export const cardMotion = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 },
} as const;

export const gradientClasses = {
  primary: 'bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5',
  primarySubtle: 'bg-gradient-to-br from-primary/10 to-primary/5',
  primaryText:
    'bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent',
  card: 'bg-gradient-to-br from-card to-card/95',
  success: 'bg-gradient-to-br from-success/20 to-success/5',
  info: 'bg-gradient-to-br from-info/20 to-info/5',
  warning: 'bg-gradient-to-br from-warning/20 to-warning/5',
  error: 'bg-gradient-to-br from-destructive/20 to-destructive/5',
  radial: 'bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))]',
  mesh: 'bg-gradient-to-br from-primary/10 via-card to-background',
} as const;

export const animationVariants = {
  fadeIn: {
    initial: { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 },
  },
  fadeInUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4 },
  },
  slideInLeft: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.3 },
  },
  slideInRight: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.3 },
  },
  scaleIn: {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.3 },
  },
  staggerContainer: {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  },
  bounce: {
    initial: { scale: 1 },
    animate: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 0.6,
        repeat: Infinity,
        repeatDelay: 2,
      },
    },
  },
} as const;

export const hoverEffects = {
  card: 'hover:scale-[1.02] transition-transform duration-300',
  cardSubtle: 'hover:scale-[1.01] transition-transform duration-200',
  button: 'hover:scale-105 active:scale-95 transition-transform duration-200',
  icon: 'hover:rotate-12 transition-transform duration-300',
  iconSpin: 'hover:rotate-180 transition-transform duration-500',
  lift: 'hover:-translate-y-1 transition-transform duration-300',
  glow: 'hover:shadow-lg hover:shadow-primary/50 transition-shadow duration-300',
  scale: 'hover:scale-105 transition-transform duration-200',
  scaleSubtle: 'hover:scale-[1.02] transition-transform duration-200',
  brightness: 'hover:brightness-110 transition-[filter] duration-200',
  shadow: 'hover:shadow-lg transition-shadow duration-200',
  borderGlow:
    'hover:border-primary/50 hover:shadow-primary/20 hover:shadow-md transition-all duration-200',
} as const;

export const transitionClasses = {
  default: 'transition-all duration-300 ease-in-out',
  fast: 'transition-all duration-150 ease-in-out',
  slow: 'transition-all duration-500 ease-in-out',
  colors: 'transition-colors duration-300',
  transform: 'transition-transform duration-300',
  opacity: 'transition-opacity duration-300',
  spring: 'transition-all duration-500 ease-spring',
} as const;

// Button press animations for tactile feedback
export const pressEffects = {
  default: 'active:scale-95 transition-transform duration-150',
  subtle: 'active:scale-[0.98] transition-transform duration-100',
  bounce: 'active:scale-90 transition-transform duration-200',
} as const;

/**
 * Loading/spinner animations
 * Note: For shimmer animation, the keyframes are already defined in tailwind.config.js:
 * animation: { shimmer: 'shimmer 2s infinite' }
 * keyframes: { shimmer: { '0%': { transform: 'translateX(-100%)' }, '100%': { transform: 'translateX(100%)' } } }
 */
export const loadingAnimations = {
  spin: 'animate-spin',
  pulse: 'animate-pulse',
  bounce: 'animate-bounce',
  shimmer: 'animate-shimmer',
} as const;

// Focus ring styles for accessibility
export const focusRing = {
  default:
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
  subtle: 'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/50',
  none: 'focus-visible:outline-none',
} as const;

// State transition classes for consistent animations
export const stateTransitions = {
  colors: 'transition-colors duration-200 ease-in-out',
  opacity: 'transition-opacity duration-200 ease-in-out',
  transform: 'transition-transform duration-200 ease-in-out',
  all: 'transition-all duration-200 ease-in-out',
  allSlow: 'transition-all duration-300 ease-in-out',
  spring: 'transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]',
} as const;

// Icon animations for interactive elements
export const iconAnimations = {
  rotate: 'hover:rotate-12 transition-transform duration-300',
  spin: 'hover:rotate-180 transition-transform duration-500',
  pulse: 'hover:scale-110 transition-transform duration-200',
  shake: 'hover:animate-[shake_0.5s_ease-in-out]',
} as const;

// Combined effect presets for common interactive patterns
export const interactivePresets = {
  button: 'hover:scale-105 active:scale-95 transition-transform duration-150',
  card: 'hover:scale-[1.02] hover:shadow-lg transition-all duration-300',
  link: 'hover:text-primary transition-colors duration-200',
  iconButton:
    'hover:bg-accent hover:text-accent-foreground active:scale-90 transition-all duration-150',
} as const;

export const shadowClasses = {
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  '2xl': 'shadow-2xl',
  inner: 'shadow-inner',
  primaryGlow: 'shadow-lg shadow-primary/50',
  primaryGlowSm: 'shadow-md shadow-primary/30',
  cardElevated: 'shadow-xl shadow-black/10',
} as const;

export const borderClasses = {
  default: 'border border-border',
  primary: 'border border-primary/20',
  accent: 'border border-accent',
  dashed: 'border border-dashed border-border',
  gradient:
    'border border-transparent bg-gradient-to-r from-primary/50 to-primary/20 bg-clip-padding',
} as const;

// Utility function to combine design tokens
export const combineTokens = (...tokens: string[]): string => {
  return tokens.filter(Boolean).join(' ');
};

// Pre-composed card styles
export const cardStyles = {
  default: combineTokens(glass.card, glass.cardHover, shadowClasses.md, transitionClasses.default),
  elevated: combineTokens(
    glass.intense,
    hoverEffects.card,
    shadowClasses.xl,
    transitionClasses.default
  ),
  gradient: combineTokens(glass.card, gradientClasses.primary, glass.cardHover, shadowClasses.lg),
} as const;

// Animation presets for common use cases
export const animationPresets = {
  page: {
    initial: { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
    transition: { duration: 0.4 },
  },
  modal: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    transition: { duration: 0.2 },
  },
  sidebar: {
    initial: { x: -300, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: -300, opacity: 0 },
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },
} as const;

/**
 * Modal-specific design tokens
 * Consistent styling for GlobalModal and dialog components
 */
export const modalTokens = {
  // Container styles
  container: {
    base: 'backdrop-blur-xl bg-card/95 border border-border shadow-2xl',
    elevated: 'backdrop-blur-xl bg-card/98 border border-primary/20 shadow-2xl',
    glass:
      'backdrop-blur-2xl bg-gradient-to-br from-card/95 via-card/90 to-card/85 border border-border',
  },

  // Header styles
  header: {
    base: 'px-6 pt-6 pb-4 border-b border-border',
    gradient:
      'px-6 pt-6 pb-4 border-b border-border bg-gradient-to-br from-primary/5 via-transparent to-transparent',
    accent: 'px-6 pt-6 pb-4 border-b border-primary/20 bg-primary/5',
  },

  // Icon container for header
  iconContainer: {
    base: 'mx-auto mb-4 p-3 rounded-xl bg-primary/10 w-fit',
    success: 'mx-auto mb-4 p-3 rounded-xl bg-success/10 w-fit',
    warning: 'mx-auto mb-4 p-3 rounded-xl bg-warning/10 w-fit',
    error: 'mx-auto mb-4 p-3 rounded-xl bg-destructive/10 w-fit',
    info: 'mx-auto mb-4 p-3 rounded-xl bg-info/10 w-fit',
  },

  // Footer styles
  footer: {
    base: 'px-6 py-4 shrink-0',
    withSeparator: 'px-6 py-4 shrink-0 border-t border-border',
    gradient: 'px-6 py-4 shrink-0 bg-gradient-to-t from-background/50 to-transparent',
  },

  // Overlay styles
  overlay: {
    base: 'bg-black/60 backdrop-blur-sm',
    heavy: 'bg-black/80 backdrop-blur-md',
    gradient: 'bg-gradient-to-br from-black/70 via-black/60 to-primary/5 backdrop-blur-sm',
  },

  // Close button styles
  closeButton: {
    base: 'absolute right-4 top-4 p-1.5 rounded-full bg-muted/50 hover:bg-muted transition-all duration-200 hover:scale-110 active:scale-95',
    subtle: 'absolute right-4 top-4 p-1 rounded-sm opacity-70 hover:opacity-100 transition-opacity',
  },

  // Body/content area
  body: {
    base: 'flex-1 min-h-0 overflow-y-auto px-6 py-4',
    noPadding: 'flex-1 min-h-0 overflow-y-auto',
    scrollable:
      'flex-1 min-h-0 overflow-y-auto px-6 py-4 scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-transparent',
  },

  // Ring/glow effects
  ring: {
    focus:
      'ring-1 ring-primary/20 focus-within:ring-2 focus-within:ring-primary/40 transition-shadow duration-300',
    glow: 'shadow-lg shadow-primary/10',
    glowStrong: 'shadow-xl shadow-primary/20',
  },
} as const;

/**
 * Modal size configurations with responsive values
 */
export const modalSizes = {
  sm: 'w-[95vw] max-w-[400px]',
  md: 'w-[95vw] max-w-[600px]',
  lg: 'w-[95vw] max-w-[700px]',
  xl: 'w-[95vw] max-w-[900px]',
  full: 'w-[95vw] max-w-[95vw] h-[90vh]',
} as const;

/**
 * Modal animation variants for Framer Motion
 */
export const modalAnimations = {
  overlay: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.2 },
  },
  content: {
    initial: { opacity: 0, scale: 0.95, y: 10 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.95, y: 10 },
    transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1] },
  },
  contentSpring: {
    initial: { opacity: 0, scale: 0.9, y: 20 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.9, y: 20 },
    transition: { type: 'spring', stiffness: 350, damping: 25 },
  },
} as const;

/**
 * Modal variant color mappings
 */
export const modalVariants = {
  default: {
    icon: 'text-primary',
    iconBg: 'bg-primary/10',
    accent: 'border-primary/20',
  },
  success: {
    icon: 'text-success',
    iconBg: 'bg-success/10',
    accent: 'border-success/20',
  },
  warning: {
    icon: 'text-warning',
    iconBg: 'bg-warning/10',
    accent: 'border-warning/20',
  },
  error: {
    icon: 'text-destructive',
    iconBg: 'bg-destructive/10',
    accent: 'border-destructive/20',
  },
  info: {
    icon: 'text-info',
    iconBg: 'bg-info/10',
    accent: 'border-info/20',
  },
} as const;
